import React, { useState, useEffect, useCallback } from 'react';
import { Megaphone, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { useAdminSettings } from '../../../hooks/useAdminSettings';

const BroadcastTab = () => {
  const { apiCall, loading, setLoading, showSuccess, showError } = useAdminSettings();
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    message: '',
    target: 'all',
    priority: 'normal'
  });

  const fetchBroadcasts = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/broadcasts');
      setBroadcasts(data.broadcasts || []);
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);

  const handleSendBroadcast = async () => {
    if (!broadcastForm.title || !broadcastForm.message) {
      showError('Please fill in title and message');
      return;
    }
    
    setLoading(true);
    try {
      await apiCall('post', '/api/admin/settings/broadcasts', broadcastForm);
      showSuccess('Broadcast sent successfully');
      setBroadcastForm({ title: '', message: '', target: 'all', priority: 'normal' });
      fetchBroadcasts();
    } catch (error) {
      showError('Failed to send broadcast');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Send Broadcast */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            Send Announcement
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm text-gray-400">Title</label>
            <Input
              value={broadcastForm.title}
              onChange={(e) => setBroadcastForm({...broadcastForm, title: e.target.value})}
              placeholder="Announcement title"
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400">Message</label>
            <textarea
              value={broadcastForm.message}
              onChange={(e) => setBroadcastForm({...broadcastForm, message: e.target.value})}
              placeholder="Write your announcement message..."
              className="w-full h-32 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400">Target Audience</label>
              <select
                value={broadcastForm.target}
                onChange={(e) => setBroadcastForm({...broadcastForm, target: e.target.value})}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
              >
                <option value="all">All Users</option>
                <option value="free">Free Tier Only</option>
                <option value="standard">Standard Tier Only</option>
                <option value="contract">Contract Tier Only</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400">Priority</label>
              <select
                value={broadcastForm.priority}
                onChange={(e) => setBroadcastForm({...broadcastForm, priority: e.target.value})}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <Button
            onClick={handleSendBroadcast}
            disabled={loading}
            className="w-full bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
          >
            <Send className="w-4 h-4 mr-2" />
            Send Broadcast
          </Button>
        </CardContent>
      </Card>

      {/* Broadcast History */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Broadcast History</CardTitle>
        </CardHeader>
        <CardContent>
          {broadcasts.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No broadcasts sent yet</p>
          ) : (
            <div className="space-y-3">
              {broadcasts.map((broadcast) => (
                <div key={broadcast.id} className="p-4 bg-gray-800/50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-white">{broadcast.title}</h4>
                    <div className="flex items-center gap-2">
                      <Badge className={
                        broadcast.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                        broadcast.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-gray-500/20 text-gray-400'
                      }>
                        {broadcast.priority}
                      </Badge>
                      <Badge className="bg-blue-500/20 text-blue-400">
                        {broadcast.target}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">{broadcast.message}</p>
                  <p className="text-xs text-gray-500">
                    Sent to {broadcast.target_count} users • {new Date(broadcast.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BroadcastTab;
